/**
 * CreateFolderDialog — Modal for creating a new wiki folder with classification.
 *
 * Extracted from knowledge.tsx. Its sensitivity and export control fields
 * follow the document rule (decision 28 / B7 + decision 37, LB6-T7): shown —
 * and sent — only while a compliance framework that shows them is active.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { LIBRARY_FOLDERS_PATH, invalidateLibraryFolders } from '@/lib/use-library-folders';
import {
  classificationFields,
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { ClassificationPolicyError } from '@/components/library/classification-policy-error';
import { toast } from 'sonner';

/**
 * The body of POST /wiki/folders. The classification keys go in ONLY for the
 * controls shown (LB6-T7) — a hidden control sends no key, not its default.
 */
export function createFolderBody(
  values: { name: string; description: string; sensitivity: string; exportControl: string },
  controls: ClassificationControlsView,
): Record<string, unknown> {
  return {
    name: values.name.trim(),
    description: values.description.trim() || undefined,
    ...classificationFields(controls, { sensitivity: values.sensitivity, exportControl: values.exportControl }),
  };
}

interface CreateFolderDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}

export function CreateFolderDialog({ open, onOpenChange, onCreated }: CreateFolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sensitivity, setSensitivity] = useState('unclassified');
  const [exportControl, setExportControl] = useState('none');
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  // Hidden (no framework that shows them): neither rendered nor sent — the
  // folder is created unclassified / none. NOT KNOWING is not hidden (review
  // LB6T7-BF-01): with no current good answer the folder is not created.
  const policy = useClassificationPolicy({ enabled: open });
  const { controls } = policy;

  const reset = () => { setName(''); setDescription(''); setSensitivity('unclassified'); setExportControl('none'); };

  const handleCreate = async () => {
    if (!name.trim() || !policy.ready) return;
    setCreating(true);
    try {
      await apiClient.post(LIBRARY_FOLDERS_PATH, createFolderBody({ name, description, sensitivity, exportControl }, controls));
      // Decision 18 — every folder reader (upload dialog, Move dialog,
      // detail page, pickers) sees the new folder.
      void invalidateLibraryFolders(queryClient);
      toast.success('Folder created');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      // The server's reason, e.g. B6's 403 INSUFFICIENT_CLEARANCE for a
      // classification above the creator's own (LB6-T3 review BF-03).
      toast.error(getApiErrorMessage(err, 'Failed to create folder'));
      // A framework turned on or off since the dialog opened: re-ask (review
      // LB6T7-BF-06).
      void invalidateClassificationPolicy(queryClient);
    }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create Folder</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Folder Name</Label>
            <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder="e.g., General Documentation" onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }} autoFocus />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input value={description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              placeholder="What kind of content goes in this folder" />
          </div>

          {/* Classification — only the controls a framework shows (LB6-T7) */}
          {controls.any ? (
          <div className="rounded-md border border-muted p-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Classification</p>
            <div className="grid grid-cols-2 gap-3">
              {controls.sensitivity ? (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Sensitivity</Label>
                <Select value={sensitivity} onValueChange={setSensitivity}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unclassified">Unclassified</SelectItem>
                    <SelectItem value="cui">CUI</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              ) : null}
              {controls.exportControl ? (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Export Control</Label>
                <Select value={exportControl} onValueChange={setExportControl}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="itar">ITAR</SelectItem>
                    <SelectItem value="ear">EAR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              ) : null}
            </div>
          </div>
          ) : null}
          {policy.unavailable ? <ClassificationPolicyError onRetry={policy.retry} /> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name.trim() || creating || !policy.ready}>{creating ? 'Creating...' : 'Create Folder'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
